import { Bot } from 'lucide-react';

interface WelcomeMessageBoxProps {
  welcomeMessage: string;
  botLogo?: string;
  primaryColor: string;
  fallbackImage: string;
  questionList?: string[];
  isLoading: boolean;
  onQuestionClick: (question: string) => void;
  showStartButton?: boolean;
  isStartingConversation?: boolean;
  onStartConversation?: () => void;
}

export default function WelcomeMessageBox({
  welcomeMessage,
  botLogo,
  primaryColor,
  fallbackImage,
  questionList,
  isLoading,
  onQuestionClick,
  showStartButton,
  isStartingConversation,
  onStartConversation,
}: WelcomeMessageBoxProps) {
  return (
    <>
      {/* Welcome Message */}
      <div className="flex gap-3 items-start">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
          style={{ backgroundColor: primaryColor }}
        >
          {botLogo ? (
            <img
              src={botLogo}
              onError={(e) => (e.currentTarget.src = fallbackImage)}
              alt="Bot"
              className="w-full h-full object-cover"
            />
          ) : (
            <Bot size={16} className="text-white" />
          )}
        </div>
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-700/50 rounded-sm rounded-tl-none px-4 py-3 shadow-sm border border-gray-100 dark:border-gray-700">
            <p className="text-sm text-gray-800 dark:text-white">{welcomeMessage}</p>
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-1">Just now</p>
        </div>
      </div>

      {/* Start Conversation Button */}
      {showStartButton && (
        <div className="flex justify-center mt-5 mb-2">
          <button
            onClick={onStartConversation}
            disabled={isStartingConversation}
            className="px-6 py-2.5 rounded-full text-white font-medium text-sm transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center min-w-[160px]"
            style={{ backgroundColor: primaryColor }}
          >
            {isStartingConversation ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                Starting...
              </span>
            ) : (
              'Start Conversation'
            )}
          </button>
        </div>
      )}

      {/* Question List - Show if available and conversation started */}
      {!showStartButton && questionList && questionList.length > 0 && (
        <div className="space-y-2 mt-4">
          <p className="text-xs font-semibold text-gray-500 px-1">Suggested questions:</p>
          {questionList.map((question, index) => (
            <button
              key={index}
              onClick={() => onQuestionClick(question)}
              disabled={isLoading}
              className="w-full text-left px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-white hover:border-gray-300 hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {question}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
